<template>
  <div class="quotation-result-page__container">
    <v-row class="insurer-price">
      <v-col cols="12" lg="5" class="divide-right px-lg-0 pr-lg-3">
        <div>
          <div class="monthly-price__wrapper">
            <v-img
              :src="require(`@/assets/images/insurers/${insurer.image}.png`)"
              transition="scale-transition"
              :max-width="90"
              :max-height="90"
              contain
              :alt="insurer.name"
              :class="[
                $vuetify.breakpoint.xs && insurer.isCheapest
                  ? 'mt-8'
                  : 'mt-sm-0',
              ]"
            />
            <div>
              <div>
                <span
                  class="monthly-price-amount"
                  :class="{
                    'monthly-price-amount--smaller':
                      insurer.selectedQuote?.MonthlyTotal >=
                      MIN_SMALLER_TEXT_PRICE,
                  }"
                  >{{
                    insurer.selectedQuote?.MonthlyTotal | formatCurrency
                  }}</span
                >&ensp;<span class="no-break-word">a month</span>
              </div>
              <div>
                <a
                  class="view-breakdown-btn"
                  @click.prevent="handleShowBreakdown"
                  >View a breakdown</a
                >
              </div>
            </div>
          </div>
          <div class="mt-2 pl-5 pl-lg-0 annual-price__wrapper">
            or you can pay annually for
            <b>{{ insurer.selectedQuote?.AnnualTotal | formatCurrency }}</b>
            a year
          </div>
        </div>
        <div>
          <div class="mt-5">
            <div class="d-flex bg-blue-50 pa-3 align-start rounded font-14">
              <v-icon color="var(--blue-500)" class="mr-2"
                >mdi-information</v-icon
              >
              <span>
                You will pay <b>{{ totalPayable | formatCurrency }}</b> in total
                if you choose to pay monthly, which means that you pay
                <b>{{ creditCharge | formatCurrency }}</b> more than if you paid
                for your policy in full today. This is because there is an
                interest charge applied when paying monthly.
                <a
                  href="javascript:"
                  class="find-out-more-dialog"
                  @click="showModal('DialogContentQuoteMoreDetail')"
                  >Find out more</a
                >
              </span>
            </div>
          </div>

          <div class="mt-4 mb-n1">
            <v-row>
              <v-col
                v-if="insurer.isAvailable && showInsurerExitBanner"
                cols="12"
                class="mb-0"
              >
                <v-alert
                  class="cut-off-message mb-0 d-flex flex-grow-1"
                  color="var(--orange-100)"
                >
                  <div class="d-flex flex-no-wrap">
                    <div class="mr-2">
                      <v-icon color="var(--orange-500)">mdi-alert</v-icon>
                    </div>
                    <div>
                      <span class="font-weight-bold">Note:</span
                      >&nbsp;Quotations provided by {{ insurer.name }} will only
                      be valid until the {{ insurerEndDate(insurer) }}.
                    </div>
                  </div>
                </v-alert>
              </v-col>
            </v-row>
          </div>
        </div>
      </v-col>
      <v-row class="mt-2 continue-container d-lg-none pb-6 px-3 px-md-7 pb-5">
        <v-col cols="12">
          <BaseButton
            text="CUSTOMISE YOUR COVER"
            rounded
            class="customize-button"
            height="60"
            :textColor="'#000000'"
            :fontSize="'font-20'"
            @click="customizeCover()"
            min-width="100%"
          />
        </v-col>
        <v-col cols="12">
          <BaseButton
            text="CONTINUE"
            rounded
            class="font-weight-bold"
            height="60"
            :color="'psorange'"
            :textColor="'#000000'"
            :fontSize="'font-20'"
            @click="handleContinue"
            min-width="100%"
          />
        </v-col>
        <v-col cols="12">
          <BaseButton
            text="SAVE QUOTE"
            flat
            depressed
            ripple
            class="font-weight-600 m-auto d-flex"
            icon="mdi-content-save"
            color="#106175"
            small
            @click="saveQuote"
            min-width="100%"
          />
        </v-col>
      </v-row>
      <v-col cols="12" lg="7">
        <v-row>
          <v-col
            cols="12"
            lg="6"
            v-if="isBuildingsCover"
            class="pb-6 pt-lg-0 px-9 px-lg-7 insurance-section"
          >
            <div class="insurance-amount">
              <h3 class="insurance-heading mb-3">Buildings Insurance</h3>
              <div class="pl-6 pl-lg-0 mb-1">
                <b>£1,000,000</b>
                cover
              </div>
              <div class="pl-6 pl-lg-0 mb-1">
                <b>{{
                  cover.buildingsCover.excess
                    | formatCurrency(AMOUNT_INSURED_FORMAT)
                }}</b>
                excess
              </div>
              <div class="pl-6 pl-lg-0 mb-1">
                <b>{{
                  quoteExcessConfiguration?.BuildingsWaterExcess
                    | formatCurrency(AMOUNT_INSURED_FORMAT)
                }}</b>
                escape of water excess
              </div>
              <div class="pl-6 pl-lg-0 mb-1">
                <b>{{
                  quoteExcessConfiguration?.BuildingsSubsidenceExcess
                    | formatCurrency(AMOUNT_INSURED_FORMAT)
                }}</b>
                subsidence excess
              </div>
            </div>
            <div class="accidential-damage mt-4">
              <div class="font-weight-bold mb-1">Accidental damage</div>
              <div class="d-flex pl-6 pl-lg-0">
                <v-icon
                  v-if="insurer.selectedQuote?.BuildingsAD"
                  class="icon darkgreen-icon"
                  >mdi-checkbox-marked-circle</v-icon
                >
                <v-icon v-else class="icon red-icon">mdi-close-circle</v-icon>
                <div
                  v-if="insurer.selectedQuote?.BuildingsAD"
                  class="ml-1 accidental-damage__text"
                >
                  Included
                </div>
                <div v-else class="ml-1 accidental-damage__text">
                  Not included
                </div>
              </div>
            </div>
          </v-col>
          <v-col
            cols="12"
            lg="6"
            v-if="isContentsCover"
            class="pb-6 pt-lg-0 px-9 px-lg-7 insurance-section d-flex flex-column"
          >
            <div class="insurance-amount">
              <h3 class="insurance-heading mb-3">Contents Insurance</h3>
              <div class="pl-6 pl-lg-0 mb-1">
                <b>{{
                  cover.contentsCover.amount
                    | formatCurrency(AMOUNT_INSURED_FORMAT)
                }}</b>
                cover
              </div>
              <div class="pl-6 pl-lg-0 mb-1">
                <b>{{
                  cover.contentsCover.excess
                    | formatCurrency(AMOUNT_INSURED_FORMAT)
                }}</b>
                excess
              </div>
              <div class="pl-6 pl-lg-0 mb-1">
                <b>{{
                  quoteExcessConfiguration?.ContentWaterExcess
                    | formatCurrency(AMOUNT_INSURED_FORMAT)
                }}</b>
                escape of water excess
              </div>
            </div>
            <div class="accidential-damage mt-4 mt-lg-auto">
              <div class="font-weight-bold mb-1">Accidental damage</div>
              <div class="d-flex pl-6 pl-lg-0">
                <v-icon
                  v-if="insurer.selectedQuote?.ContentsAD"
                  class="icon darkgreen-icon"
                  >mdi-checkbox-marked-circle</v-icon
                >
                <v-icon v-else class="icon red-icon">mdi-close-circle</v-icon>
                <div
                  v-if="insurer.selectedQuote?.ContentsAD"
                  class="ml-1 accidental-damage__text"
                >
                  Included
                </div>
                <div v-else class="ml-1 accidental-damage__text">
                  Not included
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-col>
      <v-col
        cols="12"
        class="pt-2 pb-6 px-lg-0 px-9 insurance-section additional-cover-section"
      >
        <h3 class="insurance-heading mb-3 additional-cover-heading">
          Additional cover
        </h3>
        <div
          class="pb-4 d-flex flex-column flex-lg-row justify-space-start additional-cover-items"
        >
          <div class="cover d-flex font-weight-bold additional-cover-item">
            <div class="icon-cover d-flex align-start pl-6 pl-lg-0 mr-1">
              <v-icon
                v-if="cover.landlordsEmergency"
                class="icon darkgreen-icon"
                >mdi-checkbox-marked-circle</v-icon
              >
              <v-icon v-else class="icon red-icon">mdi-close-circle</v-icon>
            </div>
            <div
              :class="[
                'cover-text',
                cover.landlordsEmergency
                  ? 'cover-text__green'
                  : 'cover-text__red',
              ]"
            >
              <div class="cover-title">Landlord’s emergency</div>
              <div v-if="cover.landlordsEmergency" class="cover-included">
                Included
              </div>
              <div v-else class="cover-included">Not included</div>
            </div>
          </div>
          <div class="cover d-flex font-weight-bold additional-cover-item">
            <div class="icon-cover d-flex align-start pl-6 pl-lg-0 mr-1">
              <v-icon v-if="cover.legalExpenses" class="icon darkgreen-icon"
                >mdi-checkbox-marked-circle</v-icon
              >
              <v-icon v-else class="icon red-icon">mdi-close-circle</v-icon>
            </div>
            <div
              :class="[
                'cover-text',
                cover.legalExpenses ? 'cover-text__green' : 'cover-text__red',
              ]"
            >
              <div class="cover-title">Legal expenses</div>
              <div v-if="cover.legalExpenses" class="cover-included">
                Included
              </div>
              <div v-else class="cover-included">Not included</div>
            </div>
          </div>
          <div class="cover d-flex font-weight-bold additional-cover-item">
            <div class="icon-cover d-flex align-start pl-6 pl-lg-0 mr-1">
              <v-icon v-if="cover.rentGuarantee" class="icon darkgreen-icon"
                >mdi-checkbox-marked-circle</v-icon
              >
              <v-icon v-else class="icon red-icon">mdi-close-circle</v-icon>
            </div>
            <div
              :class="[
                'cover-text',
                cover.rentGuarantee ? 'cover-text__green' : 'cover-text__red',
              ]"
            >
              <div class="cover-title">Rent protection</div>
              <div v-if="cover.rentGuarantee" class="cover-included">
                Included
              </div>
              <div v-else class="cover-included">Not included</div>
            </div>
          </div>
        </div>
      </v-col>
      <v-row class="mt-3 continue-container d-none d-lg-flex pb-6 pb-5">
        <v-col class="pa-0">
          <BaseButton
            text="CUSTOMISE YOUR COVER"
            rounded
            class="customize-button"
            height="60"
            :textColor="'#272727'"
            :fontSize="'font-20'"
            @click="customizeCover()"
          />
        </v-col>
        <v-col class="pa-0">
          <BaseButton
            text="CONTINUE"
            rounded
            class="font-weight-bold continue-button"
            height="60"
            :color="'psorange'"
            :textColor="'#272727'"
            :fontSize="'font-20'"
            @click="handleContinue"
          />
          <BaseButton
            text="SAVE QUOTE"
            flat
            depressed
            ripple
            class="font-weight-600 m-auto d-flex mt-4 font-20"
            icon="mdi-content-save"
            color="#106175"
            small
            @click="saveQuote"
            min-width="100%"
          />
        </v-col>
      </v-row>
    </v-row>
  </div>
</template>

<script>
import PremiumBreakdownMixin from "@/Mixins/PremiumBreakdownMixin";
import BuildingsContentsVisibility from "@/Mixins/QuoteResults/BuildingsContentsVisibility";
import { getInsurerExitDate } from "@/common/pricing";
import { MIN_SMALLER_TEXT_PRICE } from "@/constants/ui-config.constants";
import { mapMutations, mapGetters, mapState, mapActions } from "vuex";
import {
  DIALOG_MODULE,
  QUESTIONS_MODULE,
  APPLICATION_MODULE,
  STEPS_MODULE,
} from "@/store/types/module-types";
import moment from "moment";
import {
  DIRECTDEBIT,
  MONTHLY,
  SUBMITTED,
  ANNUAL,
} from "@/constants/common.constants";
import { UNSPECIFIED_PERSONAL_POSSESSIONS_D2C } from "@/constants/possessions-options";
import { AMOUNT_INSURED_FORMAT } from "@/constants/currency-format.constants";
import BaseButton from "@/components/BaseComponents/BaseButton";
import { getProductGroup } from "@/common/question-set";

export default {
  name: "QuotationResultPageTheQuote",
  components: {
    BaseButton,
  },
  props: {
    insurer: {
      type: Object,
      required: true,
    },
    cover: {
      type: Object,
      required: true,
    },
    paymentFrequency: {
      type: String,
    },
    quoteExcessConfiguration: {
      type: Object,
      required: false,
    },
  },
  mixins: [PremiumBreakdownMixin, BuildingsContentsVisibility],
  data() {
    return {
      MIN_SMALLER_TEXT_PRICE,
      AMOUNT_INSURED_FORMAT,
      SUBMITTED: SUBMITTED,
      isShowDrawerCustomizeCover: false,
    };
  },
  computed: {
    ...mapGetters(QUESTIONS_MODULE, [
      "specifiedValuables",
      "isSelfServe",
      "qsId",
    ]),
    ...mapState(APPLICATION_MODULE, ["applyResponse", "questionMap"]),
    ...mapState(STEPS_MODULE, [
      "currentStep",
      "paymentDetailStep",
      "pricesStep",
    ]),
    isPaymentStep() {
      return this.currentStep === this.paymentDetailStep;
    },
    paymentMethod() {
      return this.questionMap.PaymentMethod.Value;
    },
    innerPaymentFrequency() {
      if (this.paymentMethod === DIRECTDEBIT) {
        return this.paymentFrequency !== "" && this.paymentFrequency != null
          ? this.paymentFrequency
          : ANNUAL;
      } else {
        return ANNUAL;
      }
    },
    additionalCoverAmount() {
      if (!this.cover || !this.cover.unspecifiedPpAmount) {
        return 0;
      }
      const collection =
        this.cover.contentsCover?.amount >= this.getContentConfigD2C.max
          ? UNSPECIFIED_PERSONAL_POSSESSIONS_D2C.higherUnspecifiedAmounts
          : UNSPECIFIED_PERSONAL_POSSESSIONS_D2C.standardUnspecifiedAmounts;
      const value =
        collection.find(
          (amount) => amount.value === this.cover.unspecifiedPpAmount
        )?.numberValue ?? 0;

      return value;
    },
    breakdownModel() {
      return this.buildPremiumBreakdownModel(
        this.insurer.selectedQuote,
        DIRECTDEBIT,
        MONTHLY
      );
    },
    creditCharge() {
      return this.breakdownModel.premium?.CreditDetails?.CreditCharge;
    },
    totalPayable() {
      return this.breakdownModel.price?.Values?.TotalAmountPayable;
    },
    showInsurerExitBanner() {
      if (!this.insurer || !this.insurer.isAvailable) {
        return false;
      }
      const productGroup = getProductGroup(this.qsId);
      const exitDate = getInsurerExitDate(this.insurer, productGroup);
      return exitDate && moment(exitDate).isAfter(new Date());
    },
  },
  methods: {
    ...mapMutations(DIALOG_MODULE, ["showModal"]),
    ...mapActions(QUESTIONS_MODULE, [
      "handleSaveAndExitOnResults",
      "handleD2CSaveAndExitOnResults",
      "setOverlayVisible",
      "handleSaveQuote",
    ]),
    saveQuote() {
      this.handleSaveQuote(this.insurer).then(() => {
        const payload = {
          dialogComponentName: "D2CDialogContentSaveQuoteSuccess",
          customClass: "pb-0 dialog-contents__wrapper",
          dialogWidth: 600,
        };
        this.showModal(payload);
      });
    },
    handleContinue() {
      this.$emit("continueWithQuote", this.insurer);
    },
    insurerEndDate(insurer) {
      const productGroup = getProductGroup(this.qsId);
      const exitDate = getInsurerExitDate(insurer, productGroup);
      return exitDate ? moment(exitDate).format("Do MMMM YYYY") : "";
    },
    handleShowBreakdown() {
      if (this.isPaymentStep || this.applyResponse.QuoteStatus === SUBMITTED) {
        this.$emit(
          "showD2CPremiumBreakdown",
          this.insurer.selectedQuote,
          DIRECTDEBIT,
          this.innerPaymentFrequency
        );
      } else {
        this.$emit(
          "showD2CPremiumBreakdown",
          this.insurer.selectedQuote,
          DIRECTDEBIT,
          MONTHLY
        );
      }
    },
    customizeCover() {
      this.$emit("customiseQuote");
    },
  },
};
</script>

<style lang="scss" scoped>
.icon {
  font-size: 20px;

  &.darkgreen-icon {
    color: #1d8649;
  }

  &.red-icon {
    color: var(--red-500);
  }

  &.blue-icon {
    color: #5285c0;
  }
}

.cover-text {
  margin-left: 3px;

  &__green {
    .cover-included {
      color: #1d8649;
    }
  }

  &__red {
    .cover-included {
      color: var(--red-500);
    }
  }
}

.quotation-result-page__container {
  color: $black-2;

  .insurer-price {
    .monthly-price__wrapper {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 40px;

      .monthly-price-amount {
        text-align: left;
        font-size: 48px;
        font-weight: bold;
        font-stretch: normal;
        font-style: normal;
        line-height: normal;
        letter-spacing: 0.75px;

        &.monthly-price-amount--smaller {
          font-size: 40px;
        }
      }

      .view-breakdown-btn {
        font-size: 16px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        line-height: normal;
        letter-spacing: 0.3px;
        text-align: center;
        color: #106175 !important;
        text-decoration: underline;
      }

      .no-break-word {
        display: inline-block;
        font-size: 18px;
      }
    }

    .annual-price__wrapper {
      margin-top: 15px;
      font-size: 16px;

      b {
        font-size: 18px !important;
      }
    }

    .continue-container {
      button {
        color: $black-2;
        box-shadow: none !important;
      }

      .customize-button {
        border: 1px solid #7c869a;
        background-color: #fff;
        letter-spacing: 0.5px;
      }
    }

    .insurance-heading {
      font-size: 20px;
    }

    .insurance-section {
      border-top: 1px solid #ddd;
    }

    .icon-cover {
      padding-top: 2px;
    }

    .find-out-more-dialog {
      color: var(--blue-500) !important;
      font-weight: 700;
    }

    .rounded {
      border-radius: 10px !important;
    }
  }
}

@media screen and (min-width: $grid-breakpoints-md) {
  .quotation-result-page__container {
    .insurer-price {
      padding: 0 16px;

      .insurance-section {
        border: none;
      }
      .monthly-price__wrapper {
        padding-left: 11px;
      }

      .additional-cover-heading,
      .additional-cover-items {
        border-bottom: 1px solid #ddd;
        padding-left: 20px;

        .additional-cover-item {
          margin-right: 45px;
        }
      }

      .continue-container {
        .customize-button {
          min-width: 90%;
        }

        .continue-button {
          min-width: 100%;
        }
      }
    }
  }
}

@media screen and (min-width: $grid-breakpoints-lg) {
  .divide-right {
    border-right: 1px solid #ddd;
  }

  .quotation-result-page__container {
    .insurer-price {
      padding: 0;

      .monthly-price__wrapper {
        padding-left: 3px;
      }

      .annual-price__wrapper {
        padding-left: 7px !important;
      }

      .insurance-amount {
        min-height: 160px;
      }

      .additional-cover-section {
        min-width: 1016px;
        margin-left: -13px;
      }

      .additional-cover-items {
        .additional-cover-item {
          margin-right: 80px;
        }
      }
    }
  }
}

@media screen and (min-width: $grid-breakpoints-xl) {
  .quotation-result-page__container {
    .insurer-price {
      .continue-container {
        .customize-button {
          min-width: 450px;
        }

        .continue-button {
          min-width: 535px;
        }
      }
    }
  }
}
</style>
